<template>
  <div class="contents">
    <div class="layout">
      <h2>{{ filename }}.zip 파일 다운로드</h2>
      <h3>{{loadingMessage}}</h3>
    </div>
  </div>
</template>

<script setup>
import { useRoute } from 'vue-router'
import {apiClient} from '@/services/auth-header';
import {onMounted, ref} from 'vue';
import {saveAs} from 'file-saver';
import JSZip from 'jszip';


const API_URL = '/contents/contests';
const route = useRoute();
const filename = route.query.filename;
const loadingMessage = ref('❌ 다운로드 안함.');
let key = route.query.key;

const removeLastSlashSegment = (key) => {
  const lastSlashIndex = key.lastIndexOf('/');
  return key.substring(0, lastSlashIndex);
}

const downloadFiles = async (key, filename) => {
  const s3key = removeLastSlashSegment(key);

  try {
    const {data} = await apiClient.get(API_URL + '/file-urls', {
      params: {
        s3Key: s3key
      }
    });

    const fileUrls = data.urls ?? [];
    await downloadAttachmentsAndCreateZip(fileUrls, filename + '.zip');

  } catch (error) {
    console.error('Error fetching data:', error);
  }
};

function getRelativeZipPath(presignedUrl) {
  try {
    const url = new URL(presignedUrl);
    const fullPath = url.pathname;
    const segments = fullPath.split('/').filter(Boolean); // 빈 값 제거

    // file_로 시작하는 디렉토리가 있는 경우 그 뒤부터 사용
    const fileIndex = segments.findIndex(seg => seg.startsWith('file_'));
    if (fileIndex !== -1) {
      return decodeURIComponent(segments.slice(fileIndex).join('/'));
    }

    // file_ 디렉토리가 없으면 마지막 2개 segment만 경로로 사용 (예: 23/img.png)
    if (segments.length >= 2) {
      return decodeURIComponent(segments.slice(-2).join('/'));
    }

    // fallback: 그냥 파일 이름만 사용
    return decodeURIComponent(segments[segments.length - 1]);
  } catch (e) {
    console.warn('Invalid URL:', presignedUrl);
    return null;
  }
}

async function downloadAttachmentsAndCreateZip(urls, zipName = 'attachments.zip') {
  if (!urls || urls.length === 0) return;

  const zip = new JSZip();
  const total = urls.length;
  let completed = 0;
  const concurrency = 5;
  let currentIndex = 0;
  const startTime = performance.now();

  async function processQueue() {
    const workers = [];
    loadingMessage.value = `⏳ ${completed}/${total} 다운로드 시작...`;

    for (let i = 0; i < concurrency; i++) {
      const worker = (async () => {
        while (currentIndex < total) {
          const index = currentIndex++;
          const url = urls[index];
          if (!url) continue;

          try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('다운로드 실패: ' + url);
            const blob = await response.blob();
            const relativePath = getRelativeZipPath(url);
            if (relativePath) {
              zip.file(relativePath, blob);
            }
            completed++;
            loadingMessage.value = `⏳ ${completed}/${total} 다운로드 완료...`;
          } catch (err) {
            console.warn(`❌ ${url} 다운로드 실패`, err);
            alert(`❌ ${url} 다운로드 실패: ${err}`);
          }
        }
      })();
      workers.push(worker);
    }

    await Promise.all(workers);
  }

  try {
    await processQueue();

    loadingMessage.value = 'ZIP 압축 중...';

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, zipName);

    const endTime = performance.now();
    const elapsed = ((endTime - startTime) / 1000).toFixed(2);
    loadingMessage.value = `✅ 다운로드 완료! (${elapsed}초 소요)`;
  } catch (err) {
    console.error('ZIP 생성 실패:', err);
    alert('파일 다운로드 중 오류가 발생했습니다.');
  }
}


onMounted(async () => {
  if(confirm('다운로드 시작하기')) {
    await downloadFiles(key, filename);
  }
});

</script>

